import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import { Spinner } from "@/components/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/toast";
import {
  api,
  ApiError,
  type ScriptCheckIssue,
  type ScriptCheckResult,
  type ScriptRequirementFinding,
  type ScriptSupport,
} from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { statusBadgeVariant } from "@/lib/case-format";
import { cn } from "@/lib/utils";

const SUPPORT_LABEL: Record<ScriptSupport, string> = {
  supported: "可承载",
  partial: "部分可承载",
  unsupported: "不可承载",
};

/**
 * Ops tool: can the virtual phone present what this script requires?
 */
export function ScriptCheckPanel() {
  const auth = useAuth();
  const [script, setScript] = useState("");
  const [result, setResult] = useState<ScriptCheckResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (auth.status === "anon") return null;

  const onRun = async () => {
    if (busy) return;
    if (!script.trim()) {
      setError("请粘贴案件剧本。");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const out = await api.runScriptCheck({ script: script.trim() });
      setResult(out);
      toast.add({
        type: out.ok ? "success" : "error",
        title: out.ok ? "检测完成" : "发现不可承载项",
        description: out.summary,
      });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === "script_required") {
          setError("请粘贴案件剧本。");
        } else if (err.code === "text_too_large") {
          setError("剧本文本过大。");
        } else {
          setError(err.code);
        }
      } else {
        setError("检测失败。");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-1">
        <h1 className="font-heading text-2xl font-semibold tracking-tight">
          剧本检测
        </h1>
        <p className="text-sm text-muted-foreground">
          generate_phone 前的 schema 可承载性预检：剧本里的小手机要求能否落成
          schema.jsonc 静态 case 数据（以字段注释为唯一产品规则）。不是 pipeline
          通过，也不是结构 validate。
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>剧本文本</CardTitle>
          <CardDescription>
            粘贴完整案件剧本。系统抽取「小手机可玩要求」与可调查痕迹，对照 case
            schema 给出问题与改写建议。无手机痕迹会判为未通过。
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor="script-check-text">剧本</FieldLabel>
              <Textarea
                id="script-check-text"
                value={script}
                onChange={(e) => setScript(e.target.value)}
                placeholder="例如：玩家在 Messages 里看到与 Alex 的短信；通话记录有未接来电；相册有一张带地点的照片…"
                rows={14}
                disabled={busy}
                className="min-h-48 font-mono text-xs"
              />
              <FieldDescription>
                重点：玩家打开哪个 app、看到什么静态证据。以 schema
                注释为准（通话记录 vs 实时通话等）。纯线下道具不够成案。
              </FieldDescription>
            </Field>
            {error ? <FieldError>{error}</FieldError> : null}
          </FieldGroup>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              disabled={busy || !script.trim()}
              onClick={() => void onRun()}
            >
              {busy ? <Spinner /> : null}
              {busy ? "检测中…" : "开始检测"}
            </Button>
            {result ? (
              <Button
                type="button"
                variant="outline"
                disabled={busy}
                onClick={() => {
                  setResult(null);
                  setError(null);
                }}
              >
                清除结果
              </Button>
            ) : null}
            <Button
              variant="outline"
              render={<Link to="/cases/new" />}
              nativeButton={false}
            >
              去新建案件
            </Button>
          </div>
        </CardContent>
      </Card>

      {result ? <ScriptCheckResultView result={result} /> : null}
    </div>
  );
}

function ScriptCheckResultView({ result }: { result: ScriptCheckResult }) {
  return (
    <div className="flex flex-col gap-4">
      <Alert variant={result.ok ? "default" : "destructive"}>
        <AlertTitle>
          {result.ok ? "预检通过（非 pipeline 通过）" : "预检未通过"}
        </AlertTitle>
        <AlertDescription>
          <p>{result.summary}</p>
          <p className="mt-1 text-muted-foreground">模型：{result.model}</p>
        </AlertDescription>
      </Alert>

      {result.issues.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>问题与改写建议</CardTitle>
            <CardDescription>
              错误 = 阻断（schema 表达不了或缺少手机痕迹）；提醒 = 部分支持，进
              generate 前请确认。
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            {result.issues.map((issue, index) => (
              <div
                key={`${issue.code}-${index}`}
                className={cn(
                  "rounded-lg border px-3 py-2",
                  issue.severity === "error" && "border-destructive/40",
                )}
              >
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant={statusBadgeVariant(issue.severity)}>
                    {issue.severity === "error"
                      ? "错误"
                      : issue.severity === "warn"
                        ? "提醒"
                        : "信息"}
                  </Badge>
                  <span className="text-sm font-medium">{issue.title}</span>
                  {issue.schemaPath ? (
                    <span className="font-mono text-[0.65rem] text-muted-foreground">
                      {issue.schemaPath}
                    </span>
                  ) : null}
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  {issue.detail}
                </p>
                {issue.suggestion ? (
                  <p className="mt-1 text-xs">建议：{issue.suggestion}</p>
                ) : null}
              </div>
            ))}
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>抽取到的小手机要求</CardTitle>
          <CardDescription>
            每条是剧本期望玩家在手机上看到或做到的事，以及对照 schema 的承载判断。
          </CardDescription>
        </CardHeader>
        <CardContent>
          {result.findings.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              未识别到小手机相关要求。
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>剧本要求</TableHead>
                  <TableHead>schema 路径</TableHead>
                  <TableHead>承载</TableHead>
                  <TableHead>说明 / 改写</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {result.findings.map((f, index) => (
                  <TableRow key={`${f.schemaPath ?? "x"}-${index}`}>
                    <TableCell className="align-top">
                      <p className="font-medium">{f.requirement}</p>
                      {f.evidence ? (
                        <p className="mt-0.5 max-w-xs text-xs text-muted-foreground">
                          {f.evidence}
                        </p>
                      ) : null}
                    </TableCell>
                    <TableCell className="align-top font-mono text-xs">
                      {f.schemaPath ?? "—"}
                    </TableCell>
                    <TableCell className="align-top">
                      <Badge variant={statusBadgeVariant(f.support)}>
                        {SUPPORT_LABEL[f.support]}
                      </Badge>
                    </TableCell>
                    <TableCell className="align-top max-w-sm text-xs text-muted-foreground">
                      <p>{f.note}</p>
                      {f.rewriteHint ? (
                        <p className="mt-1 text-foreground">
                          改写：{f.rewriteHint}
                        </p>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Case schema</CardTitle>
          <CardDescription>
            权威来源：apps/dash/case-spec/schema.jsonc（含字段注释）。
          </CardDescription>
        </CardHeader>
        <CardContent>
          <pre className="max-h-96 overflow-auto whitespace-pre-wrap rounded-md border px-3 py-2 font-mono text-[0.65rem] leading-relaxed text-muted-foreground">
            {result.caseSchema}
          </pre>
        </CardContent>
      </Card>
    </div>
  );
}
